/**
 * usePerformance — lightweight WebSocket performance monitoring hook.
 *
 * Tracks:
 *   - Round-trip latency per query (send → first response)
 *   - Reconnection count and cumulative duration
 *   - Message throughput (messages received per second, rolling window)
 *
 * Uses window.performance.now() for high-resolution timing.
 */
import { useRef, useCallback, useMemo } from 'react';
import { createLogger } from '../utils/logger';

const log = createLogger('Performance');

const THROUGHPUT_WINDOW_MS = 60_000; // 1-minute rolling window

export function usePerformance() {
  // Map<queryId, startTime>
  const pendingQueries = useRef(new Map());
  const latencyHistory = useRef([]);
  const reconnections = useRef({ count: 0, totalMs: 0, lastStart: null });
  const messageTimes = useRef([]);

  // ── Latency ──────────────────────────────────────────────────────────────

  /** Call when a query is sent. */
  const markQuerySent = useCallback((queryId) => {
    pendingQueries.current.set(queryId, performance.now());
  }, []);

  /** Call when the first response for a query arrives. Returns latency in ms. */
  const markQueryResponse = useCallback((queryId) => {
    const start = pendingQueries.current.get(queryId);
    if (start == null) return null;
    pendingQueries.current.delete(queryId);
    const latencyMs = Math.round(performance.now() - start);
    latencyHistory.current.push({ queryId, latencyMs, ts: Date.now() });
    // Keep last 100 entries
    if (latencyHistory.current.length > 100) latencyHistory.current.shift();
    log.debug('query_latency', { queryId, latencyMs });
    return latencyMs;
  }, []);

  // ── Reconnection ────────────────────────────────────────────────────────

  const markReconnectStart = useCallback(() => {
    reconnections.current.lastStart = performance.now();
  }, []);

  const markReconnectEnd = useCallback(() => {
    const start = reconnections.current.lastStart;
    if (start == null) return;
    const durationMs = Math.round(performance.now() - start);
    reconnections.current.count += 1;
    reconnections.current.totalMs += durationMs;
    reconnections.current.lastStart = null;
    log.info('reconnect', {
      count: reconnections.current.count,
      durationMs,
      totalMs: reconnections.current.totalMs,
    });
  }, []);

  // ── Throughput ──────────────────────────────────────────────────────────

  /** Call on every inbound WS message. */
  const recordMessage = useCallback(() => {
    const now = Date.now();
    messageTimes.current.push(now);
    // Prune outside window
    const cutoff = now - THROUGHPUT_WINDOW_MS;
    messageTimes.current = messageTimes.current.filter((t) => t >= cutoff);
  }, []);

  // ── Snapshot ────────────────────────────────────────────────────────────

  /** Return a snapshot of all metrics. */
  const getMetrics = useCallback(() => {
    const now = Date.now();
    const cutoff = now - THROUGHPUT_WINDOW_MS;
    const recentMessages = messageTimes.current.filter((t) => t >= cutoff);
    const windowSec = THROUGHPUT_WINDOW_MS / 1000;

    const latencies = latencyHistory.current;
    const avgLatency =
      latencies.length > 0
        ? Math.round(latencies.reduce((s, e) => s + e.latencyMs, 0) / latencies.length)
        : null;

    return {
      latency: {
        avg: avgLatency,
        last: latencies.length > 0 ? latencies[latencies.length - 1].latencyMs : null,
        count: latencies.length,
      },
      reconnections: {
        count: reconnections.current.count,
        totalMs: reconnections.current.totalMs,
      },
      throughput: {
        messagesPerSec: +(recentMessages.length / windowSec).toFixed(2),
        totalInWindow: recentMessages.length,
      },
    };
  }, []);

  return useMemo(
    () => ({
      markQuerySent,
      markQueryResponse,
      markReconnectStart,
      markReconnectEnd,
      recordMessage,
      getMetrics,
    }),
    [markQuerySent, markQueryResponse, markReconnectStart, markReconnectEnd, recordMessage, getMetrics],
  );
}

export default usePerformance;
